/**
 * Unified-filter model — the ordered list of filterable dimensions shown in the
 * Add-filter panel's left rail (and used to label the unified chip rail).
 *
 * Derived from the single column registry (`columnFilterTypes.js`) so a new
 * filterable column can't silently diverge in label or editor type. The display
 * order and grouping mirror the approved prototype: attributes first, then
 * financials, then location & status. `businessVerticals` (Key end-markets) is
 * intentionally absent — it's not in `COLUMN_FILTER_TYPE_BY_KEY` yet, so it is
 * excluded here by construction.
 */

import { COLUMN_FILTER_TYPE_BY_KEY, COLUMN_LABEL_BY_KEY } from './columnFilterTypes';

/**
 * Grouped dimension keys, in display order. Keys that aren't registered as
 * filterable (no entry in `COLUMN_FILTER_TYPE_BY_KEY`) are dropped when the flat
 * `FILTER_DIMENSIONS` list is built, so this stays a superset-safe source order.
 */
export const FILTER_DIMENSION_GROUPS = Object.freeze([
  Object.freeze({ label: 'Attributes', keys: Object.freeze(['industries', 'valueChain', 'products']) }),
  Object.freeze({
    label: 'Financials',
    keys: Object.freeze(['revenue', 'employees', 'ebitPercentage', 'cagr3Y'])
  }),
  Object.freeze({
    label: 'Location & status',
    keys: Object.freeze(['headquarters', 'situation', 'registerStatus'])
  })
]);

/**
 * @typedef {Object} FilterDimension
 * @property {string} key   Column key (matches the engine's `company[key]`).
 * @property {string} label Display label (from `COLUMN_LABEL_BY_KEY`).
 * @property {string} type  Editor type (from `COLUMN_FILTER_TYPE_BY_KEY`).
 * @property {string} group Group label this dimension belongs to.
 */

/**
 * Flat, ordered list of dimensions — the panel rail iterates this. Built from
 * the groups so order + grouping stay in one place; any key not registered as
 * filterable is skipped.
 * @type {ReadonlyArray<FilterDimension>}
 */
export const FILTER_DIMENSIONS = Object.freeze(
  FILTER_DIMENSION_GROUPS.flatMap((group) =>
    group.keys
      .filter((key) => COLUMN_FILTER_TYPE_BY_KEY[key])
      .map((key) =>
        Object.freeze({
          key,
          label: COLUMN_LABEL_BY_KEY[key] ?? key,
          type: COLUMN_FILTER_TYPE_BY_KEY[key],
          group: group.label
        })
      )
  )
);

/**
 * Lookup a dimension descriptor by column key. Returns undefined for a key that
 * isn't a registered filterable dimension.
 */
export function dimensionForKey(key) {
  return FILTER_DIMENSIONS.find((d) => d.key === key);
}
